import type { HTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from 'react';
import type { SortDirection, SortState } from './use-sort';

type Align = 'left' | 'right' | 'center';

// Full class names on purpose: Tailwind only generates classes it can see in the source.
const ALIGN: Record<Align, string> = { left: 'text-left', right: 'text-right', center: 'text-center' };

export function Table({ className = '', ...props }: HTMLAttributes<HTMLTableElement>) {
  return (
    <div className="overflow-x-auto">
      <table {...props} className={`w-full border-collapse whitespace-nowrap text-sm ${className}`} />
    </div>
  );
}

export function Th({
  className = '',
  align = 'right',
  ...props
}: ThHTMLAttributes<HTMLTableCellElement> & { align?: Align }) {
  return (
    <th
      {...props}
      className={`border border-slate-200 bg-slate-50 px-2 py-1.5 font-semibold ${ALIGN[align]} ${className}`}
    />
  );
}

export function Td({
  className = '',
  align = 'right',
  ...props
}: TdHTMLAttributes<HTMLTableCellElement> & { align?: Align }) {
  return <td {...props} className={`border border-slate-200 px-2 py-1.5 tabular-nums ${ALIGN[align]} ${className}`} />;
}

interface SortableThProps<K extends string> extends Omit<ThHTMLAttributes<HTMLTableCellElement>, 'onClick'> {
  align?: Align;
  sortKey: K;
  sort: SortState<K>;
  onSort(key: K, first: SortDirection): void;
  // Where a first click lands: newest and biggest first for dates and figures, A–Z for names.
  first?: SortDirection;
}

export function SortableTh<K extends string>({
  sortKey,
  sort,
  onSort,
  first = 'desc',
  children,
  ...props
}: SortableThProps<K>) {
  const active = sort.key === sortKey;
  const ariaSort = active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none';
  return (
    <Th {...props} aria-sort={ariaSort}>
      <button
        type="button"
        onClick={() => onSort(sortKey, first)}
        className="inline-flex items-center gap-1 font-semibold hover:text-blue-700"
      >
        {children}
        <span aria-hidden="true" className={active ? 'text-blue-700' : 'text-slate-300'}>
          {active && sort.direction === 'asc' ? '▲' : '▼'}
        </span>
      </button>
    </Th>
  );
}
